import { CPTSlabForManualOption } from "../../../views/Orders/enums";
import {
  CptAudit,
  MovementKind,
  SelectedScheduleSnapshot,
} from "../types/cptAudit.types";
import { DeliveryOption } from "../types";
import { normalizeCptSlabValue } from "./deliveryOptionCptSlabUtils";

export interface ReadyTimeBreakdown {
  crtMinutes: number;
  srtMinutes: number;
  qrtMinutes: number;
  totalMinutes: number;
}

export interface SrtFormulaParts {
  baseTimeMinutes: number;
  /** Distinct line items in the estimate split (not total quantity). */
  lineItems: number;
  perItemMinutes: number;
  resultMinutes: number;
  formula: string;
}

const MOVEMENT_KIND_LABELS: Record<MovementKind, string> = {
  [MovementKind.SAME_SUPPLIER]: "Same Supplier",
  [MovementKind.WAREHOUSE_TO_OUTLET]: "Warehouse → Outlet",
  [MovementKind.OUTLET_TO_WAREHOUSE]: "Outlet → Warehouse",
  [MovementKind.SAME_ROUTE_OUTLET_TO_OUTLET]: "Same Route Outlet → Outlet",
  [MovementKind.DIFFERENT_ROUTE_OUTLET_TO_OUTLET]:
    "Different Route Outlet → Outlet",
  [MovementKind.UNSUPPORTED]: "Unsupported",
};

const SRT_FORMULA_REGEX =
  /^(\d+)\s*\+\s*\(\s*(\d+)\s*-\s*(\d+)\s*\)\s*×\s*(\d+)\s*=\s*(-?\d+)\s*$/;

export function isStandardDeliveryOption(
  option: Pick<DeliveryOption, "cptSlab">,
): boolean {
  return (
    normalizeCptSlabValue(option.cptSlab) ===
    CPTSlabForManualOption.STANDARD_DELIVERY
  );
}

export function shouldShowCptAuditInfo(
  option: Pick<DeliveryOption, "cptSlab" | "cptAudit">,
): option is DeliveryOption & { cptAudit: CptAudit } {
  const slab = normalizeCptSlabValue(option.cptSlab);
  return (
    option.cptAudit != null &&
    (slab === CPTSlabForManualOption.STANDARD_DELIVERY ||
      slab === CPTSlabForManualOption.SELF_PICKUP)
  );
}

export type GaraazLastMileDisplay = "slot" | "deliveryTime" | "hidden";

export function resolveGaraazLastMileDisplay(params: {
  hasGaraazSlot: boolean;
  hasGaraazDeliveryTime: boolean;
}): GaraazLastMileDisplay {
  if (params.hasGaraazSlot) {
    return "slot";
  }
  if (params.hasGaraazDeliveryTime) {
    return "deliveryTime";
  }
  return "hidden";
}

export function formatMinutesShort(minutes: number): string {
  return `${minutes} min`;
}

export function formatMovementKind(movementKind: string): string {
  return (
    MOVEMENT_KIND_LABELS[movementKind as MovementKind] ??
    movementKind.replaceAll("_", " ")
  );
}

export function getReadyTimeBreakdown(audit: CptAudit): ReadyTimeBreakdown {
  return {
    crtMinutes: audit.crtMinutes,
    srtMinutes: audit.srtMinutes,
    qrtMinutes: audit.qrtMinutes,
    totalMinutes: audit.crtMinutes + audit.srtMinutes + audit.qrtMinutes,
  };
}

export function parseSrtFormula(formula: string): SrtFormulaParts | null {
  const match = formula.trim().match(SRT_FORMULA_REGEX);
  if (!match) {
    return null;
  }

  return {
    baseTimeMinutes: Number(match[1]),
    lineItems: Number(match[2]),
    perItemMinutes: Number(match[4]),
    resultMinutes: Number(match[5]),
    formula: formula.trim(),
  };
}

export function formatSlotLabel(slotId: string): string {
  const normalized = slotId.trim();
  const slotNumber = normalized.replace(/^[Ss](?:lot)?/i, "");

  return `Slot ${slotNumber || normalized}`;
}

export function addIsoDateDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

/** Last selected schedule is the dest inbound leg. */
export function pickDestinationSchedule(
  schedules: SelectedScheduleSnapshot[] | undefined,
): SelectedScheduleSnapshot | undefined {
  if (!schedules?.length) {
    return undefined;
  }
  return schedules[schedules.length - 1];
}

function ordinalDay(day: number): string {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/**
 * Today / Tomorrow / Yesterday, otherwise `Fri, 28th Aug`.
 */
export function formatReadableSlotDay(
  isoDate: string,
  todayIso: string,
): string {
  const diff = diffCalendarDays(todayIso, isoDate);
  if (diff === 0) {
    return SCHEDULE_DAY_LABELS.today;
  }
  if (diff === 1) {
    return SCHEDULE_DAY_LABELS.tomorrow;
  }
  if (diff === -1) {
    return SCHEDULE_DAY_LABELS.yesterday;
  }

  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = date.toLocaleDateString("en-GB", {
    weekday: "short",
    timeZone: "UTC",
  });
  const monthShort = date.toLocaleDateString("en-GB", {
    month: "short",
    timeZone: "UTC",
  });
  return `${weekday}, ${ordinalDay(day)} ${monthShort}`;
}

export function formatReadableSlotLabel(
  isoDate: string,
  slotLabel: string,
  todayIso: string,
): string {
  const dayLabel = formatReadableSlotDay(isoDate, todayIso);
  return dayLabel ? `${dayLabel}, ${slotLabel}` : slotLabel;
}

export function formatDestMovementSlotLabel(
  schedule: SelectedScheduleSnapshot,
  todayIso: string = getTodayIsoDate(),
): string {
  const arrivalDate = schedule.dispatchDate
    ? addIsoDateDays(schedule.dispatchDate, schedule.cutoffInDayOffset ?? 0)
    : "";
  const slotLabel = formatSlotLabel(schedule.slotId);
  return arrivalDate
    ? formatReadableSlotLabel(arrivalDate, slotLabel, todayIso)
    : slotLabel;
}

export function formatDayOffsetSuffix(dayOffset: number): string {
  return dayOffset > 0 ? ` (+${dayOffset})` : "";
}

export const SCHEDULE_DAY_LABELS = {
  today: "Today",
  tomorrow: "Tomorrow",
  yesterday: "Yesterday",
} as const;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local calendar date as YYYY-MM-DD (defaults to now). */
export function getTodayIsoDate(reference: Date = new Date()): string {
  const year = reference.getFullYear();
  const month = String(reference.getMonth() + 1).padStart(2, "0");
  const day = String(reference.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Whole calendar days between two YYYY-MM-DD dates (toIso − fromIso). */
export function diffCalendarDays(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((to - from) / MS_PER_DAY);
}

/**
 * Actual-day label for a leg date relative to today.
 * Today / Tomorrow / Yesterday, otherwise the real date (e.g. "Sat, 19 Jul").
 */
export function formatDayLabel(isoDate: string, todayIso: string): string {
  const diff = diffCalendarDays(todayIso, isoDate);
  if (diff === 0) {
    return SCHEDULE_DAY_LABELS.today;
  }
  if (diff === 1) {
    return SCHEDULE_DAY_LABELS.tomorrow;
  }
  if (diff === -1) {
    return SCHEDULE_DAY_LABELS.yesterday;
  }

  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export type ScheduleAction = "in" | "out";

export interface FormattedScheduleRow {
  businessName: string;
  routeSlotLabel: string;
  /** Role of this supplier in the movement: dispatching (out) or receiving (in). */
  action: ScheduleAction;
  /** Cutoff time for the supplier's action in this movement. */
  actionTime: string;
  /** Day-offset suffix for inbound legs only (e.g. " (+1)"). */
  actionTimeSuffix: string;
  cutoffOut: string;
  cutoffIn: string;
  cutoffInDayOffsetSuffix: string;
  /** Calendar days the In leg lands after the Out (dispatch) day. 0 = same day. */
  cutoffInDayOffset: number;
  /** Calendar day (YYYY-MM-DD) this leg dispatches on. Empty for legacy audits. */
  dispatchDate: string;
}

export interface ScheduleDayGroup {
  /** Dispatch day shared by the legs in this group. Empty for legacy audits. */
  dispatchDate: string;
  rows: FormattedScheduleRow[];
}

/**
 * Groups ordered schedule legs into consecutive same-day buckets so the UI can
 * show one day header (Today / Tomorrow / date) per group instead of repeating
 * the day on every In/Out line.
 */
export function groupScheduleRowsByDay(
  rows: FormattedScheduleRow[],
): ScheduleDayGroup[] {
  const groups: ScheduleDayGroup[] = [];

  for (const row of rows) {
    const currentGroup = groups[groups.length - 1];
    if (currentGroup && currentGroup.dispatchDate === row.dispatchDate) {
      currentGroup.rows.push(row);
    } else {
      groups.push({ dispatchDate: row.dispatchDate, rows: [row] });
    }
  }

  return groups;
}

/**
 * Ordered schedule legs alternate dispatch → receive:
 * source Out, dest In; cross-route warehouse appears as In then Out.
 */
export function resolveScheduleAction(scheduleIndex: number): ScheduleAction {
  return scheduleIndex % 2 === 0 ? "out" : "in";
}

export function formatScheduleRow(
  schedule: SelectedScheduleSnapshot,
  scheduleIndex = 0,
): FormattedScheduleRow {
  const action = resolveScheduleAction(scheduleIndex);
  const cutoffInDayOffsetSuffix = formatDayOffsetSuffix(
    schedule.cutoffInDayOffset,
  );

  return {
    businessName: schedule.supplierName,
    routeSlotLabel: `${schedule.routeId} • ${formatSlotLabel(schedule.slotId)}`,
    action,
    actionTime: action === "out" ? schedule.cutoffOut : schedule.cutoffIn,
    actionTimeSuffix: action === "in" ? cutoffInDayOffsetSuffix : "",
    cutoffOut: schedule.cutoffOut,
    cutoffIn: schedule.cutoffIn,
    cutoffInDayOffsetSuffix,
    cutoffInDayOffset: schedule.cutoffInDayOffset,
    dispatchDate: schedule.dispatchDate ?? "",
  };
}
